export const EASE_PREMIUM = [0.22, 1, 0.36, 1];

export const defaultViewport = {
  once: true,
  // `amount: 'some'` fires as soon as any part of the target enters the
  // viewport. A numeric fraction (e.g. 0.18) requires that fraction of the
  // element to be visible at once, which is impossible for sections taller
  // than the viewport — on mobile those sections stack tall and would stay
  // stuck at opacity:0, leaving a blank gap. 'some' is height-independent.
  //
  // A large POSITIVE bottom margin expands the trigger area far below the
  // viewport, so content starts revealing ~800px BEFORE it scrolls into view —
  // by the time you reach it, it is already there, with no blank gap even on
  // tall/large monitors.
  //
  // IMPORTANT: use PIXELS, not %. framer-motion's viewport `margin` does not
  // reliably honour percentage values (they can be silently ignored), which is
  // why percentage-based buffers left below-the-fold sections looking blank.
  amount: 'some',
  margin: '0px 0px 800px 0px',
};

export const premiumTransition = (delay = 0, duration = 0.7) => ({
  duration,
  delay,
  ease: EASE_PREMIUM,
});

// TRANSFORM-ONLY reveals — deliberately NO opacity/blur in the hidden state.
//
// Why: reveals used to fade in from opacity:0 via `whileInView`. On client-side
// navigation the IntersectionObserver occasionally didn't fire on mount, leaving
// content stuck at opacity:0 (missing until a refresh). Keeping content fully
// opaque and animating only a small transform makes that impossible — if a
// reveal ever fails to fire, the worst case is a ~26px offset, never a blank.
export const revealVariants = {
  fadeUp: {
    hidden: { y: 26 },
    visible: { y: 0 },
  },
  fadeIn: {
    hidden: { y: 14 },
    visible: { y: 0 },
  },
  // NOTE: slide reveals deliberately use a VERTICAL offset, not horizontal.
  // whileInView occasionally fails to fire on client-side navigation, leaving a
  // reveal stuck in its hidden state (see the transform-only note above). A
  // stuck horizontal offset shifts content sideways and gets clipped by the
  // page's overflow-x:hidden — most visibly on mobile, where 32px is a large
  // fraction of the width. A stuck vertical offset is harmless. Keeping the
  // keys lets call sites stay expressive without risking horizontal clipping.
  slideLeft: {
    hidden: { y: 26 },
    visible: { y: 0 },
  },
  slideRight: {
    hidden: { y: 26 },
    visible: { y: 0 },
  },
  scaleUp: {
    hidden: { scale: 0.975 },
    visible: { scale: 1 },
  },
};

export const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
};

export const staggerItem = {
  hidden: revealVariants.fadeUp.hidden,
  visible: {
    ...revealVariants.fadeUp.visible,
    transition: premiumTransition(0, 0.6),
  },
};

export function getRevealProps(variant = 'fadeUp', delay = 0) {
  const v = revealVariants[variant] || revealVariants.fadeUp;
  return {
    initial: v.hidden,
    whileInView: v.visible,
    viewport: defaultViewport,
    transition: premiumTransition(delay),
  };
}
